import { Parameter } from "domain/entities/parameter";
import { Project } from "domain/entities/project";
import { ProjectMember } from "domain/entities/project-member";
import { EmployeeInputFilter } from "domain/models/employee-input-filter";
import { ProjectInputFilter } from "domain/models/project-input-filter";
import { ProjectMemberInputFilter } from "domain/models/project-member-input-filter";
import DropdownComponent from "presentation/components/Shared/DropdownComponent";
import InputCurrencyComponent from "presentation/components/Shared/InputCurrencyComponent";
import InputDateComponent from "presentation/components/Shared/InputDateComponent";
import InputTextComponent from "presentation/components/Shared/InputTextComponent";
import MultiSelectComponent from "presentation/components/Shared/MultiSelectComponent";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import { useProjectsByFilter } from "presentation/hooks/use-query-project";
import { useProjectMembersByFilter } from "presentation/hooks/use-query-project-member";
import { usePurchaseOrderConsumptionPreview } from "presentation/hooks/use-query-purchase-order";
import { useParameterStore } from "presentation/stores/use-parameter-store";
import { usePurchaseOrderStore } from "presentation/stores/use-purchase-order-store";
import { useEffect, useState } from "react";
import { InputNumber } from "primereact/inputnumber";

const PurchaseOrderInfoForm = () => {
  const { data: projectsData, refetch: refetchProjects } = useProjectsByFilter({
    limit: 100,
  } as ProjectInputFilter);
  const [selectedProjectId, setSelectedProjectId] = useState<string>();
  const currentProject = projectsData?.data?.find(p => p.id === selectedProjectId);
  const { purchaseOrder, setPurchaseOrder } = usePurchaseOrderStore();
  const { data: projectMembersData, refetch: refetchProjectMember } =
    useProjectMembersByFilter({
      projectId: selectedProjectId,
      limit: 1000,
      sortBy: "employee.name",
      startRange: purchaseOrder?.startAt,
      endRange: purchaseOrder?.endAt,
    } as ProjectMemberInputFilter);
  const { purchaseOrderStatus, purchaseOrderType } = useParameterStore();
  const [projects, setProjects] = useState<Parameter[]>();
  const [currentMember, setCurrentMember] = useState();
  const [projectMembers, setProjectMembers] = useState<Parameter[]>();
  const [selectedEmployees, setSelectedEmployees] = useState<any>([]);
  const [initialHasLimit, setInitialHasLimit] = useState<boolean | null>(null);

  const isEditing = !!purchaseOrder?.id;
  const hasConsumption =
    purchaseOrder?.id &&
    purchaseOrder?.plafondLeft !== undefined &&
    purchaseOrder?.plafond !== undefined &&
    purchaseOrder.plafondLeft !== purchaseOrder.plafond;

  // Bloqueio de edição só ocorre se a PO já carregou originalmente com hasLimit=Sim (ou true) E com consumo registrado.
  // Se carregou como provisória (initialHasLimit === false), continua permitindo edição mesmo ao alterar para Sim.
  const wasLoadedWithLimit = initialHasLimit === true;
  const cannotEditEverything = isEditing && !!hasConsumption && wasLoadedWithLimit;
  const isNoLimitPO = initialHasLimit === false || purchaseOrder?.hasLimit === false;

  const { data: consumptionPreviewData, isLoading: isLoadingPreview } = usePurchaseOrderConsumptionPreview(purchaseOrder?.id);

  useEffect(() => {
    if (purchaseOrder?.id && initialHasLimit === null && purchaseOrder?.hasLimit !== undefined) {
      setInitialHasLimit(Boolean(purchaseOrder.hasLimit));
    }
  }, [purchaseOrder?.id, purchaseOrder?.hasLimit, initialHasLimit]);

  useEffect(() => {
    if (purchaseOrder && !hasConsumption && purchaseOrder.plafond !== undefined && purchaseOrder.hasLimit !== false) {
      if (purchaseOrder.plafondLeft !== purchaseOrder.plafond) {
        setPurchaseOrder({
          ...purchaseOrder,
          plafondLeft: purchaseOrder.plafond,
        });
      }
    }
  }, [purchaseOrder?.plafond, hasConsumption, purchaseOrder?.hasLimit]);

  const sortProjMembersByName = (ids: string[]): string[] => {
    const sortedIds = ids.sort((a, b) => {
      const nameA = projectMembers?.find((pm) => pm.id === a)?.value;
      const nameB = projectMembers?.find((pm) => pm.id === b)?.value;
      if (nameA && nameB) return nameA.localeCompare(nameB);
      return 0;
    });
    return sortedIds;
  };

  const { data: activeEmployees} = useEmployeesByFilter({limit: 100, isActive: true} as EmployeeInputFilter);

  useEffect(() => {
    const projectMembersDataActive = projectMembersData?.data?.filter((po) => activeEmployees?.data?.some(emp => emp.id === po.employee?.id));
    const formatedProjectMembers = projectMembersDataActive?.map((item) => {
      const employeeName = item.employee?.name;
      const start = item.startAt
        ? new Date(item.startAt).toISOString().split("T")[0]
        : "";
      const end = item.endAt
        ? new Date(item.endAt).toISOString().split("T")[0]
        : "";

      const employee: Parameter = {
        id: item.id,
        value: employeeName ? `${employeeName} (${start} <> ${end})` : "",
        type: "projectMember",
      };

      return employee;
    });
    setProjectMembers(formatedProjectMembers);
  }, [projectMembersData, activeEmployees]);

  useEffect(() => {
    const formatedProjects = projectsData?.data?.map((item) => {
      const project: Parameter = {
        id: item.id,
        value: item.name,
        type: "project",
      };

      return project;
    });
    setProjects(formatedProjects);
  }, [projectsData]);

  useEffect(() => {
    if (purchaseOrder?.type) {
      const isDeslocacaoOrPrevencao = 
        purchaseOrder.type === "Deslocação" || 
        purchaseOrder.type === "Prevenção" ||
        purchaseOrder.type === "DESLOCACAO" ||
        purchaseOrder.type === "PREVENCAO";

      if (
        purchaseOrder.type === "Serviço" ||
        purchaseOrder.type === "Interno" ||
        purchaseOrder.type === "Hora Extra" 
      ) {
        purchaseOrder.fixedValue = null;
      } else {
        purchaseOrder.fixedValue = 0;
      }

      if (isDeslocacaoOrPrevencao) {
        purchaseOrder.hasLimit = false;
        purchaseOrder.plafond = 0;
        purchaseOrder.plafondLeft = 0;
        purchaseOrder.plafondUnit = "CURRENCY";
      }

      setPurchaseOrder(purchaseOrder);
    }
  }, [purchaseOrder?.type]);

  useEffect(() => {
    if (
      purchaseOrder?.projectMembers &&
      purchaseOrder?.projectMembers?.length > 0
    ) {
      const firstMember = purchaseOrder.projectMembers[0];
      const projectId = firstMember.project?.id;
      if (projectId) {
        setSelectedProjectId(projectId);
      }
    }
  }, [purchaseOrder?.projectMembers]);

  useEffect(() => {
    if (
      purchaseOrder?.projectMembers &&
      purchaseOrder?.projectMembers?.length > 0 &&
      projectMembers &&
      projectMembers?.length > 0
    ) {
      const matchedIds = purchaseOrder.projectMembers
        .map((pm) => {
          const match = projectMembers.find((p) => p.id === pm.id);
          return match?.id;
        })
        .filter((id): id is string => Boolean(id));

      const sortedIds = sortProjMembersByName(matchedIds);
      const isDifferent =
        selectedEmployees.length !== sortedIds.length ||
        selectedEmployees.some((val: string, index: number) => val !== sortedIds[index]);

      if (isDifferent) {
        setSelectedEmployees(sortedIds);
      }
    }
  }, [projectMembers]);

  useEffect(() => {
    if (purchaseOrder && selectedEmployees.length > 0) {
      const newProjectMembers = selectedEmployees.map((id: string) => {
        const existing = purchaseOrder.projectMembers?.find(pm => pm.id === id);
        return {
          id,
          hourlyRate: existing?.hourlyRate ?? 0
        };
      });

      const isDifferent =
        !purchaseOrder.projectMembers ||
        purchaseOrder.projectMembers.length !== newProjectMembers.length ||
        purchaseOrder.projectMembers.some((pm, index) => {
          const npm = newProjectMembers[index];
          return pm.id !== npm?.id || pm.hourlyRate !== npm?.hourlyRate;
        });

      if (isDifferent) {
        const updatedPO = {
          ...purchaseOrder,
          projectMembers: newProjectMembers,
        };

        setPurchaseOrder(updatedPO);
      }
    }
  }, [selectedEmployees]);



  const consumption = consumptionPreviewData?.data;

  return (
    <div className="default-grid ">
      {purchaseOrder?.id && initialHasLimit === false && purchaseOrder?.hasLimit === true && consumption && (consumption.totalHours > 0 || consumption.totalOccurrences > 0) && (
        <div
          className="default-4-cols"
          style={{
            background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
            border: "1px solid #90caf9",
            borderRadius: "8px",
            padding: "0.75rem 1.25rem",
            marginBottom: "0.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1.5rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flex: 1, minWidth: 0 }}>
            <i className="pi pi-info-circle text-blue-600" style={{ fontSize: "1.25rem", flexShrink: 0 }} />
            <div>
              <strong style={{ color: "#1565c0", fontSize: "0.92rem", display: "block" }}>
                PO Provisória (Sem Limite) - Levantamento de Apontamentos da Timesheet
              </strong>
              <div style={{ fontSize: "0.82rem", color: "#475569", marginTop: "2px", lineHeight: "1.25" }}>
                Ao definir um limite de Plafond ('Sim') e salvar, o saldo da PO será calculado retroativamente com base nas horas já apontadas e nas taxas definidas.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: "1.25rem", alignItems: "center", background: "white", padding: "0.4rem 0.85rem", borderRadius: "6px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", flexShrink: 0 }}>
            <div style={{ textAlign: "center" }}>
              <span style={{ fontSize: "0.72rem", color: "#64748b", textTransform: "uppercase", display: "block", fontWeight: 600 }}>Total Apontado</span>
              <strong style={{ fontSize: "0.95rem", color: "#1e293b" }}>{consumption.totalHours.toFixed(1)} h</strong>
            </div>
            <div style={{ width: "1px", height: "24px", background: "#e2e8f0" }} />
            <div style={{ textAlign: "center" }}>
              <span style={{ fontSize: "0.72rem", color: "#64748b", textTransform: "uppercase", display: "block", fontWeight: 600 }}>Custo Apontado</span>
              <strong style={{ fontSize: "0.95rem", color: "#1e293b" }}>
                {new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(consumption.totalCost)}
              </strong>
            </div>
          </div>
        </div>
      )}

      <div className="default-1-col">
        <InputTextComponent
          label="Nome"
          fieldName="name"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.name}
          setValue={setPurchaseOrder}
          isDisable={cannotEditEverything}
        />
      </div>
      <div className="default-1-col">
        <InputTextComponent
          label="Número"
          fieldName="poNumber"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.poNumber}
          setValue={setPurchaseOrder}
          isDisable={cannotEditEverything}
        />
      </div>
      <div className="default-1-col">
        <DropdownComponent
          options={purchaseOrderType}
          label="Tipo"
          fieldName="type"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.type}
          setValue={setPurchaseOrder}
          isDisable={cannotEditEverything}
        />
      </div>
      <div className="default-1-col">
        <DropdownComponent
          options={projects ?? []}
          label="Projeto"
          fieldName="project"
          currentObject={{}}
          getValue={selectedProjectId}
          setValue={(updatedObj) => {
            const projId = updatedObj.project.id;
            setSelectedProjectId(projId);
            const selectedProj = projectsData?.data?.find(p => p.id === projId);
            if (selectedProj && purchaseOrder) {
              setPurchaseOrder({
                ...purchaseOrder,
                startAt: selectedProj.startAt ? new Date(selectedProj.startAt) : undefined,
                endAt: selectedProj.endAt ? new Date(selectedProj.endAt) : undefined
              });
            }
          }}
          isValueId={true}
          isDisable={cannotEditEverything}
        />
      </div>
      <div className="default-1-col">
        <DropdownComponent
          options={[
            { id: "true", value: "Sim", type: "hasLimit" },
            { id: "false", value: "Não", type: "hasLimit" }
          ]}
          label="Tem limite de Plafond?"
          fieldName="hasLimit"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.hasLimit !== undefined ? String(purchaseOrder.hasLimit) : "true"}
          setValue={setPurchaseOrder}
          isValueId={true}
          isDisable={
            cannotEditEverything ||
            purchaseOrder?.type === "Deslocação" ||
            purchaseOrder?.type === "Prevenção"
          }
          replaceArray={[
            { from: "true", to: true },
            { from: "false", to: false }
          ]}
        />
      </div>
      <div className="default-1-col">
        <DropdownComponent
          options={[
            { id: "CURRENCY", value: "Moeda (€)", type: "plafondUnit" },
            { id: "HOURS", value: "Horas (h)", type: "plafondUnit" }
          ]}
          label="Unidade de Plafond"
          fieldName="plafondUnit"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.plafondUnit || "CURRENCY"}
          setValue={setPurchaseOrder}
          isValueId={true}
          fieldNameId="id"
          isDisable={cannotEditEverything}
        />
      </div>
      <div className="default-1-col">
        <InputCurrencyComponent
          label={purchaseOrder?.plafondUnit === "HOURS" ? "Horas Total" : "Valor Total"}
          fieldName="plafond"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.plafond}
          setValue={setPurchaseOrder}
          isDisable={cannotEditEverything}
          mode={purchaseOrder?.plafondUnit === "HOURS" ? "decimal" : "currency"}
          suffix={purchaseOrder?.plafondUnit === "HOURS" ? " h" : undefined}
        />
      </div>

      <div className="default-1-col">
        <InputCurrencyComponent
          label="Valor fixo diário"
          fieldName="fixedValue"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.fixedValue}
          setValue={setPurchaseOrder}
          isDisable={
            cannotEditEverything ||
            purchaseOrder?.type === "Serviço" ||
            purchaseOrder?.type === "Interno" ||
            purchaseOrder?.type === "Hora Extra"
          }
        />
      </div>
      <div className="default-1-col">
        <InputCurrencyComponent
          label="Saldo Disponível"
          fieldName="plafondLeft"
          isDisable={true}
          currentObject={purchaseOrder}
          getValue={
            purchaseOrder?.id
              ? purchaseOrder?.plafondLeft
              : purchaseOrder?.plafond
          }
          setValue={setPurchaseOrder}
          mode={purchaseOrder?.plafondUnit === "HOURS" ? "decimal" : "currency"}
          suffix={purchaseOrder?.plafondUnit === "HOURS" ? " h" : undefined}
        />
      </div>
      <div className="default-1-col">
        <DropdownComponent
          options={purchaseOrderStatus}
          label="Status"
          fieldName="status"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.status}
          setValue={setPurchaseOrder}
          isDisable={true}
        />
      </div>
      <div className="default-1-col">
        <InputDateComponent
          label="Data de início"
          fieldName="startAt"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.startAt}
          setValue={setPurchaseOrder}
          isDisable={cannotEditEverything}
          minDate={currentProject?.startAt ? new Date(currentProject.startAt) : undefined}
          maxDate={purchaseOrder?.endAt ? new Date(purchaseOrder.endAt) : (currentProject?.endAt ? new Date(currentProject.endAt) : undefined)}
        />
      </div>
      <div className="default-1-col">
        <InputDateComponent
          label="Data de término"
          fieldName="endAt"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.endAt}
          setValue={setPurchaseOrder}
          minDate={purchaseOrder?.startAt ? new Date(purchaseOrder.startAt) : (currentProject?.startAt ? new Date(currentProject.startAt) : undefined)}
          maxDate={currentProject?.endAt ? new Date(currentProject.endAt) : undefined}
        />
      </div>
      <div className="default-4-cols">
        <MultiSelectComponent
          options={projectMembers ?? []}
          label="Colaboradores"
          isValueId={true}
          fieldName="projectMember"
          currentObject={currentMember}
          getValue={selectedEmployees}
          setValue={(ids) => {
            const sorted = sortProjMembersByName(ids);
            setSelectedEmployees(sorted);
          }}
          isDisable={cannotEditEverything || !selectedProjectId}
        />
      </div>

      {purchaseOrder?.plafondUnit === "HOURS" ? null : (
        purchaseOrder?.projectMembers && purchaseOrder.projectMembers.length > 0 && (
          <div className="default-4-cols" style={{ marginTop: "1rem" }}>
            <h4 style={{ marginBottom: "0.5rem", color: "var(--text-color)" }}>Taxa Horária por Colaborador (€/h)</h4>
            <div style={{ display: "grid", gap: "0.5rem" }}>
              {purchaseOrder.projectMembers.map((member) => {
                const matchedPM = projectMembers?.find((pm) => pm.id === member.id);
                if (!matchedPM) return null;
                return (
                  <div key={member.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", padding: "0.5rem", border: "1px solid var(--surface-border)", borderRadius: "4px" }}>
                    <span style={{ fontSize: "0.9rem", color: "var(--text-color)" }}>{matchedPM.value}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <span style={{ fontSize: "0.85rem", color: "var(--text-color-secondary)" }}>Taxa Horária:</span>
                      <div style={{ width: "120px" }}>
                        <InputNumber
                          value={member.hourlyRate ?? 0}
                          onChange={(e) => {
                            const updatedMembers = purchaseOrder.projectMembers?.map((m) => 
                              m.id === member.id ? { ...m, hourlyRate: e.value !== null ? e.value : undefined } : m
                            );
                            setPurchaseOrder({
                              ...purchaseOrder,
                              projectMembers: updatedMembers
                            });
                          }}
                          mode="currency"
                          currency="EUR"
                          locale="pt-PT"
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default PurchaseOrderInfoForm;
